import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { randomId } from '@/lib/utils/id'
import { ToastContext, type ToastInput, type ToastVariant } from './toastContext'

interface ToastItem extends Required<Omit<ToastInput, 'description'>> {
  id: string
  description?: string
}

const VARIANT_STYLES: Record<ToastVariant, { icon: typeof Info; className: string }> = {
  success: { icon: CheckCircle2, className: 'text-emerald-600 dark:text-emerald-400' },
  error: { icon: XCircle, className: 'text-red-600 dark:text-red-400' },
  warning: { icon: AlertTriangle, className: 'text-amber-600 dark:text-amber-400' },
  info: { icon: Info, className: 'text-brand-600 dark:text-brand-300' },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id))
  }, [])

  const toast = useCallback(
    (input: ToastInput) => {
      const item: ToastItem = {
        id: randomId(),
        title: input.title,
        description: input.description,
        variant: input.variant ?? 'info',
        durationMs: input.durationMs ?? 5_000,
      }
      setItems((current) => [...current.slice(-3), item])
      window.setTimeout(() => dismiss(item.id), item.durationMs)
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2"
        >
          {items.map((item) => {
            const { icon: Icon, className } = VARIANT_STYLES[item.variant]
            return (
              <div
                key={item.id}
                className="pointer-events-auto flex animate-slide-up items-start gap-3 rounded-xl border border-border bg-card p-3 shadow-card-hover"
              >
                <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', className)} aria-hidden />
                <div className="flex-1">
                  <p className="text-sm font-medium">{item.title}</p>
                  {item.description ? (
                    <p className="mt-0.5 text-xs text-muted-foreground">{item.description}</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(item.id)}
                  aria-label="Dispensar aviso"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )
          })}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
